import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardHeader, 
  Col, 
  Container, 
  Row, 
  Table, 
  Button, 
  Badge, 
  Alert,
  Spinner
} from 'reactstrap';
import { fetchOrders } from '../../../utils/order';
import { getLoggedinUser } from '../../../utils/api_helper_custom';
import CODPaymentConfirmation from '../../../Components/Common/CODPaymentConfirmation';
import BreadCrumb from '../../../Components/Common/BreadCrumb';

const CODManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadOrders();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = () => {
    const user = getLoggedinUser();
    setCurrentUser(user);
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchOrders(1, 100); // Get more orders for COD management
      
      if (response.success) {
        // Filter for COD orders
        const codOrders = response.orders?.filter(order => 
          order.stripe_payment_status === 'cod'
        ) || [];
        setOrders(codOrders);
      } else {
        setError(response.message || 'Failed to load orders');
      }
    } catch (err) {
      setError(err.message || 'Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const canConfirmCOD = () => {
    if (!currentUser) return false;
    const allowedRoles = ['employee2', 'employee3', 'superadmin'];
    return allowedRoles.includes(currentUser.role || currentUser.user_type);
  };

  const getCODPendingOrders = () => {
    return orders.filter(order => 
      order.stripe_payment_status === 'cod' && 
      order.status === 'cod_pending'
    );
  };

  const getCODConfirmedOrders = () => {
    return orders.filter(order => 
      order.stripe_payment_status === 'cod' && 
      order.status === 'cod_confirmed'
    );
  };

  const handleConfirmPayment = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handlePaymentConfirmed = (updatedOrder) => {
    // Update the order in the list
    setOrders(orders.map(order => 
      order.id === updatedOrder.id ? updatedOrder : order
    ));
    setSelectedOrder(null);
    setIsModalOpen(false);
  };

  const handleError = (errorMessage) => {
    console.error('COD Payment Error:', errorMessage);
    // Error is already handled in the modal component
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'cod_pending': { color: 'warning', text: 'Payment Pending' },
      'cod_confirmed': { color: 'success', text: 'Payment Confirmed' },
      'pending_payment': { color: 'info', text: 'Pending Payment' },
      'confirmed': { color: 'success', text: 'Confirmed' }
    };
    
    const statusInfo = statusMap[status] || { color: 'secondary', text: status };
    return <Badge color={statusInfo.color}>{statusInfo.text}</Badge>;
  };

  if (!canConfirmCOD()) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="COD Management" pageTitle="Order Management" />
          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <Alert color="danger">
                    <h4>Access Denied</h4>
                    <p>You do not have permission to access COD payment management.</p>
                    <p>Only Employee2, Employee3, and SuperAdmin can perform this action.</p>
                  </Alert>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="COD Management" pageTitle="Order Management" />
          <Row>
            <Col xl={12}>
              <Card>
                <CardBody className="text-center py-5">
                  <Spinner color="primary" className="me-2" />
                  Loading COD orders...
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="COD Management" pageTitle="Order Management" />
          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <Alert color="danger">
                    <h4>Error Loading Orders</h4>
                    <p>{error}</p>
                    <Button color="primary" onClick={loadOrders}>
                      Try Again
                    </Button>
                  </Alert>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  const pendingOrders = getCODPendingOrders();
  const confirmedOrders = getCODConfirmedOrders();

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="COD Management" pageTitle="Order Management" />
        
        <Row>
          <Col xl={12}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center">
                  <h5 className="card-title flex-grow-1 mb-0">
                    Cash on Delivery (COD) Payment Management
                  </h5>
                  <div className="flex-shrink-0">
                    <Button color="primary" onClick={loadOrders}>
                      <i className="ri-refresh-line align-middle me-1"></i>
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                {/* Summary Cards */}
                <Row className="mb-4">
                  <Col md={4}>
                    <Card className="card-animate">
                      <CardBody>
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium mb-0">Pending COD Payments</p>
                            <h4 className="mt-2 mb-0">{pendingOrders.length}</h4>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="avatar-sm rounded-circle bg-warning-subtle">
                              <span className="avatar-title rounded-circle bg-warning text-white">
                                <i className="ri-time-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="card-animate">
                      <CardBody>
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium mb-0">Confirmed COD Payments</p>
                            <h4 className="mt-2 mb-0">{confirmedOrders.length}</h4>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="avatar-sm rounded-circle bg-success-subtle">
                              <span className="avatar-title rounded-circle bg-success text-white">
                                <i className="ri-check-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="card-animate">
                      <CardBody>
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium mb-0">Total COD Orders</p>
                            <h4 className="mt-2 mb-0">{orders.length}</h4>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="avatar-sm rounded-circle bg-info-subtle">
                              <span className="avatar-title rounded-circle bg-info text-white">
                                <i className="ri-shopping-cart-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Pending COD Orders */}
                <div className="mb-4">
                  <h5 className="mb-3">Pending COD Payments ({pendingOrders.length})</h5>
                  {pendingOrders.length === 0 ? (
                    <Alert color="info">
                      No pending COD payments at the moment.
                    </Alert>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-nowrap align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Order Reference</th>
                            <th>Customer</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingOrders.map(order => (
                            <tr key={order.id}>
                              <td>
                                <strong>{order.order_reference_id}</strong>
                              </td>
                              <td>
                                {order.user?.name || order.user?.first_name || 'N/A'}
                              </td>
                              <td>
                                <strong>${order.total_price}</strong>
                              </td>
                              <td>
                                {getStatusBadge(order.status)}
                              </td>
                              <td>
                                {formatDate(order.created_at)}
                              </td>
                              <td>
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => handleConfirmPayment(order)}
                                >
                                  <i className="ri-check-line align-middle me-1"></i>
                                  Confirm Payment
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>

                {/* Confirmed COD Orders */}
                <div>
                  <h5 className="mb-3">Confirmed COD Payments ({confirmedOrders.length})</h5>
                  {confirmedOrders.length === 0 ? (
                    <Alert color="info">
                      No confirmed COD payments yet.
                    </Alert>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-nowrap align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Order Reference</th>
                            <th>Customer</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Payment Confirmed</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {confirmedOrders.map(order => (
                            <tr key={order.id}>
                              <td>
                                <strong>{order.order_reference_id}</strong>
                              </td>
                              <td>
                                {order.user?.name || order.user?.first_name || 'N/A'}
                              </td>
                              <td>
                                <strong>${order.total_price}</strong>
                              </td>
                              <td>
                                {getStatusBadge(order.status)}
                              </td>
                              <td>
                                {order.paid_at ? formatDate(order.paid_at) : 'N/A'}
                              </td>
                              <td>
                                {formatDate(order.created_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* COD Payment Confirmation Modal */}
        <CODPaymentConfirmation
          orderId={selectedOrder?.id}
          orderReferenceId={selectedOrder?.order_reference_id}
          isOpen={isModalOpen}
          toggle={() => setIsModalOpen(false)}
          onConfirm={handlePaymentConfirmed}
          onError={handleError}
        />
      </Container>
    </div>
  );
};

export default CODManagement;
